/**
 * Boletos por unidade — envio do mês (gestão) — 0.1.47.
 *
 * O síndico/gestor sobe o PDF combinado (todos os boletos, um por página), igual a
 * "processar um novo mês". A tela faz um PRÉ-CHECK (não grava) e mostra o relatório
 * de consistência; só então confirma o envio. Depois de enviado, o morador baixa só
 * o boleto do apto dele no Portal (botão DOC).
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, FileText, Info, Loader2, Trash2, Upload,
} from "lucide-react";
import { api } from "../lib/api";

interface Relatorio {
  total_paginas: number;
  total_unidades: number;
  total_casados: number;
  faltantes: string[];
  sobras: string[];
  duplicados: string[];
  competencia_divergente: { unidade: string; competencia: string }[];
  vencidos: { unidade: string; vencimento: string }[];
  sem_unidade: number[];
  tem_alertas: boolean;
}
interface Analise { ano: number; mes: number; tipo: string; relatorio: Relatorio; itens: unknown[] }
interface Lote {
  id: number; ano: number; mes: number; tipo: string; competencia: string;
  arquivo_nome: string; total_paginas: number; total_unidades: number;
  total_casados: number; tem_alertas: boolean; criado_por_nome: string | null; criado_em: string | null;
}

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export function BoletosPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [tipo, setTipo] = useState<"NORMAL" | "EXTRA">("NORMAL");
  const [file, setFile] = useState<File | null>(null);
  const [analise, setAnalise] = useState<Analise | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [lotes, setLotes] = useState<Lote[]>([]);

  async function carregarLotes() {
    try { setLotes(await api.get<Lote[]>(`/condominios/${condominioId}/boletos`)); }
    catch { /* silencioso */ }
  }
  useEffect(() => { void carregarLotes(); }, [condominioId]);

  function _form(): FormData {
    const fd = new FormData();
    fd.append("arquivo", file as File);
    fd.append("ano", String(ano));
    fd.append("mes", String(mes));
    fd.append("tipo", tipo);
    return fd;
  }

  async function conferir() {
    if (!file) { setErro("Escolha o PDF com todos os boletos."); return; }
    setErro(null); setOk(null); setAnalise(null); setOcupado(true);
    try {
      setAnalise(await api.postForm<Analise>(`/condominios/${condominioId}/boletos/analisar`, _form()));
    } catch (e) { setErro((e as Error).message || "Falha ao analisar o PDF."); }
    finally { setOcupado(false); }
  }

  async function enviar() {
    if (!file) return;
    setErro(null); setOcupado(true);
    try {
      await api.postForm<Lote>(`/condominios/${condominioId}/boletos/enviar`, _form());
      setOk(`Boletos de ${MESES[mes - 1]}/${ano} publicados. Os moradores já podem baixar o DOC deles.`);
      setAnalise(null); setFile(null);
      await carregarLotes();
    } catch (e) { setErro((e as Error).message || "Falha ao enviar."); }
    finally { setOcupado(false); }
  }

  async function remover(id: number) {
    if (!confirm("Remover este envio de boletos? Os moradores deixarão de baixar por aqui.")) return;
    try { await api.delete(`/boletos/lote/${id}`); await carregarLotes(); }
    catch (e) { setErro((e as Error).message || "Falha ao remover."); }
  }

  const rel = analise?.relatorio;

  return (
    <div className="max-w-3xl mx-auto px-3 py-4">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <FileText size={20} className="text-brand-700" /> Boletos do mês (2ª via)
        </h1>
      </div>

      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900 mb-4 flex gap-2">
        <Info size={18} className="flex-shrink-0 mt-0.5" />
        <div>
          Suba aqui o <b>PDF único com todos os boletos</b> que a administradora enviou (um boleto por página).
          O sistema separa por apartamento e cada morador passa a baixar <b>só o dele</b> no Portal (botão <b>DOC</b>).
          É uma <b>cópia</b> — o documento oficial continua sendo o e-mail da administradora.
        </div>
      </div>

      {/* Formulário de envio */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Mês</span>
            <select value={mes} onChange={(e) => setMes(Number(e.target.value))} className="border rounded-md px-2 py-1.5">
              {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Ano</span>
            <input type="number" value={ano} onChange={(e) => setAno(Number(e.target.value))} className="border rounded-md px-2 py-1.5 w-24" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Tipo</span>
            <select value={tipo} onChange={(e) => setTipo(e.target.value as "NORMAL" | "EXTRA")} className="border rounded-md px-2 py-1.5">
              <option value="NORMAL">Mensal (normal)</option>
              <option value="EXTRA">Chamada extra</option>
            </select>
          </label>
          <label className="text-sm flex-1 min-w-[220px]">
            <span className="block text-slate-600 mb-1">PDF com todos os boletos</span>
            <input type="file" accept="application/pdf"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setAnalise(null); setOk(null); }}
              className="block w-full text-sm" />
          </label>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={conferir} disabled={ocupado || !file}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 text-white text-sm font-medium disabled:opacity-50">
            {ocupado ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Conferir (pré-check)
          </button>
        </div>
        {erro && <p className="mt-2 text-sm text-red-700">{erro}</p>}
        {ok && <p className="mt-2 text-sm text-green-700 flex items-center gap-1"><CheckCircle2 size={16} /> {ok}</p>}
      </div>

      {/* Relatório do pré-check */}
      {rel && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl font-bold text-slate-900">{rel.total_casados}</span>
            <span className="text-slate-500">de {rel.total_unidades} unidades com boleto ({rel.total_paginas} páginas no PDF)</span>
          </div>

          {!rel.tem_alertas && rel.faltantes.length === 0 && (
            <p className="text-sm text-green-700 flex items-center gap-1 mb-2">
              <CheckCircle2 size={16} /> Tudo certo: todos os apartamentos têm boleto e sem divergências.
            </p>
          )}

          {rel.faltantes.length > 0 && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900 mb-2">
              <div className="font-medium flex items-center gap-1"><Info size={15} /> Sem boleto neste PDF ({rel.faltantes.length}): {rel.faltantes.join(", ")}</div>
              <div className="text-blue-800/90 mt-1">Pode ser normal — morador que paga antecipado, unidade isenta ou boleto que virá depois. Confira antes de publicar.</div>
            </div>
          )}
          {rel.sobras.length > 0 && (
            <Alerta titulo={`Apartamentos fora do cadastro (${rel.sobras.length})`} itens={rel.sobras}
              dica="Esses boletos não casaram com nenhuma unidade cadastrada. Confira o nome do apto no cadastro." />
          )}
          {rel.duplicados.length > 0 && (
            <Alerta titulo={`Unidades com boleto repetido (${rel.duplicados.length})`} itens={rel.duplicados}
              dica="A mesma unidade apareceu em mais de uma página." />
          )}
          {rel.competencia_divergente.length > 0 && (
            <Alerta titulo={`Competência diferente do mês escolhido (${rel.competencia_divergente.length})`}
              itens={rel.competencia_divergente.map((c) => `${c.unidade} → ${c.competencia}`)}
              dica="Verifique se selecionou o mês certo, ou se o PDF é de outro mês." />
          )}
          {rel.vencidos.length > 0 && (
            <Alerta titulo={`Boletos já vencidos (${rel.vencidos.length})`}
              itens={rel.vencidos.map((v) => `${v.unidade} → ${v.vencimento}`)}
              dica="A data de vencimento já passou. Confirme se é o mês correto." />
          )}
          {rel.sem_unidade.length > 0 && (
            <Alerta titulo={`Páginas sem unidade identificada (${rel.sem_unidade.length})`}
              itens={rel.sem_unidade.map((p) => `página ${p}`)}
              dica="Não foi possível ler a unidade nessas páginas — confira o PDF." />
          )}

          <button onClick={enviar} disabled={ocupado}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold disabled:opacity-50">
            {ocupado ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Confirmar e publicar para os moradores
          </button>
        </div>
      )}

      {/* Envios já publicados */}
      <h2 className="text-sm font-semibold text-slate-700 mb-2">Envios publicados</h2>
      {lotes.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhum envio ainda.</p>
      ) : (
        <div className="space-y-2">
          {lotes.map((l) => (
            <div key={l.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <div>
                <span className="font-medium text-slate-900">{MESES[l.mes - 1]}/{l.ano}{l.tipo === "EXTRA" ? " (extra)" : ""}</span>
                <span className="text-slate-500"> · {l.total_casados}/{l.total_unidades} unidades · {l.total_paginas} boletos</span>
                {l.tem_alertas && <span className="ml-2 inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={13} /> revisar</span>}
              </div>
              <button onClick={() => remover(l.id)} title="Remover este envio"
                className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Alerta({ titulo, itens, dica }: { titulo: string; itens: string[]; dica: string }) {
  return (
    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 mb-2">
      <div className="font-medium flex items-center gap-1"><AlertTriangle size={15} /> {titulo}</div>
      <div className="mt-1">{itens.join(", ")}</div>
      <div className="text-amber-800/90 mt-1">{dica}</div>
    </div>
  );
}
